import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Dispatch } from 'redux';
import { IStore } from '../../../../../redux/store';
import moment from 'moment';
import { inspectorSelectors } from 'redux/app/vessel/inspector';
import { vesselSelectors } from 'redux/app/vessel';

type OwnProps = {
    children: React.ReactNode;
	vesselId: string;
};
type BFL1CoverPageProps = PropsFromRedux & OwnProps;
function BFL1CoverPage(props: BFL1CoverPageProps) {
    const {
        dateRange,
        vesselName,
        imoNumber,
        distinctiveNumberOrLetters,
        children,
    } = props;

    return (
        <div>
            {/* First Page */}
            <div className="flex flex-col justify-between px-8 py-4 mb-2">
                <div className="mx-0 my-24 text-xl font-medium text-center">
                    <h3 className="uppercase">
                        PART I - Biofouling management activities
                    </h3>
                </div>
                <div className="flex justify-center mt-8 mb-32">
                    <div className="w-2/3 mt-2.5">
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mb-0 mr-2 min-w-fit">Name of ship:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0">
                                {vesselName}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mb-0 mr-2 min-w-fit">
                                Distinctive number or letters:
                            </p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {distinctiveNumberOrLetters}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mb-0 mr-2 min-w-fit">IMO Number:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0">
                                {imoNumber}
                            </p>
                        </div>

                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mb-0 mr-2 min-w-fit">
                                Gross tonnage:
                            </p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {props?.tonnage?.grossTonnage || ''}
                            </p>
                        </div>
                        <div
                            className="flex items-center justify-between mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <div
                                style={{ width: '54%' }}
                                className="flex items-center mb-2"
                            >
                                <p className="mr-2 min-w-fit">Period from :</p>
                                <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                    {dateRange?.startDate &&
                                        moment(dateRange?.startDate).format(
                                            'DD-MM-yyyy'
                                        )}{' '}
                                </p>
                            </div>
                            <div
                                style={{ width: '45%' }}
                                className="flex items-center mb-2"
                            >
                                <p className="mr-2 min-w-fit">to :</p>
                                <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                    {dateRange?.endDate &&
                                        moment(dateRange?.endDate).format(
                                            'DD-MM-yyyy'
                                        )}
                                </p>
                            </div>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">Working language of the ship:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                English
                            </p>
                        </div>
                    </div>
                </div>

                <div className="border p-1 my-4 border-[#2D3748]">
                    Note: Biofouling Record Book Part I should be provided to
                    every ship with a Biofouling Management Plan (BFMP), to
                    record relevant biofouling activities such as inspections,
                    maintenance and cleaning activities. Biofouling Record Book
                    Part II should also be provided to record when the ship has
                    a higher risk of biofouling accumulation and related
                    contingency actions
                </div>
            </div>
            {/* Second Page */}
            <div className="flex flex-col justify-between px-8 py-4 pt-0 mb-2 text-left newpage">
                <h2 className="text-xl font-medium">Introduction</h2>
                <p className="py-2">
                    The following pages of this section show a comprehensive
                    list of items of biofouling management activities which are,
                    when appropriate, to be recorded in Biofouling Record Book
                    Part I. Management of biofouling should be in line with an
                    approved Biofouling Management Plan (BFMP) and take into
                    account guidelines developed by the Organization. The items
                    have been grouped into operational sections, each of which
                    is denoted by a letter code.
                </p>
                <p className="py-2">
                    When making entries in Biofouling Record Book Part I, the
                    date, operational code and item number should be inserted in
                    the appropriate columns and the required particulars should
                    be recorded chronologically in the blank spaces. Each
                    completed operation should be signed for and dated by the
                    officer or officers in charge. The master of the ship should
                    sign each completed page.
                </p>
                <p className="py-2">
                    The use of an electronic record book to record activities is
                    an alternative method to a hard copy record book. Electronic
                    recording and reporting should be encouraged as it may have
                    many benefits and may allow ships to utilize their
                    technology to reduce administrative burdens and contribute
                    to onboard environmental initiatives, e.g. reduction of
                    paper use. In case electronic recording is to be used,
                    resolution MEPC.312(74) may be used for guidance.
                </p>
                <p className="py-2">
                    Biofouling Record Book Part I contains many references to
                    observations regarding fouling rating. These observations
                    may be included in separate reports including observations
                    of subsections and corresponding photos/video. The entries
                    in Biofouling Record Book Part I may be a summary only
                    including a conclusion on whether the activity is in line
                    with the BFMP. Biofouling Record Book Part I should be kept
                    on board the ship in a place where it is readily available
                    for inspection at all reasonable times and for the life of
                    the ship. Any inspection of Biofouling Record Book Part I
                    should be performed as expeditiously as possible without
                    causing the ship to be unduly delayed.
                </p>
            </div>
            {/* Third Page */}
            <div className="px-8 py-0 newpage">
                <h2 className="mb-6 text-2xl font-medium">
                    LIST OF ITEMS TO BE RECORDED
                </h2>
                {/* <!-- Section A --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">(A) Proactive cleaning.</h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>
                            Date and location of ship when proactive cleaning
                            occurred.
                        </li>
                        <li>
                            General observations with regard to biofouling prior
                            to cleaning, if any (i.e. extent of microfouling and
                            macrofouling in line with the defined ratings).
                        </li>
                        <li>
                            Records of permits required to undertake in-water
                            proactive cleaning, if applicable.
                        </li>
                        <li>Details of hull and niche areas cleaned.</li>
                        <li>
                            General observations with regard to biofouling after
                            the cleaning, if any (i.e. extent of microfouling
                            and macrofouling in line with the defined ratings).
                        </li>
                        <li>
                            Reference to any supporting evidence/reports of the
                            cleaning (e.g. report from supplier,
                            photographs/videos and/or receipts), if any.
                        </li>
                        <li>
                            Method, manufacturer and model of proactive cleaning
                            method used, if not given in BFMP.
                        </li>
                        <li>
                            Reference to test standard for which the method has
                            been tested, if not given in BFMP.
                        </li>
                        <li>
                            Name, position and signature of the person in charge
                            of the activity.
                        </li>
                    </ol>
                </div>

                {/* <!-- Section B --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">(B) Inspection</h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>Date and location of inspection.</li>
                        <li>
                            Methods used for inspection (including inspection
                            tools/devices).
                        </li>
                        <li>Areas inspected of the ship.</li>
                        <li>
                            Observations with regard to biofouling (extent of
                            microfouling and macrofouling in line with the
                            defined fouling rates).
                        </li>
                        <li>
                            Observations with regard to anti-fouling system
                            (AFS) condition.
                        </li>
                        <li>
                            Reference to any supporting evidence/reports of the
                            inspection.
                        </li>
                        <li>
                            Name, position and signature of the person in charge
                            of the activity.
                        </li>
                    </ol>
                </div>

                {/* <!-- Section C --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">(C) Reactive cleaning</h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>
                            Date and location of ship when cleaning occurred.
                        </li>
                        <li>
                            Records of permits required to undertake in-water
                            cleaning, if applicable.
                        </li>
                        <li>Description of hull and niche areas cleaned.</li>
                        <li>Methods of reactive cleaning used</li>
                        <li>
                            Estimation of overall biofouling after cleaning in
                            line with the defined fouling rates.
                        </li>
                        <li>
                            Reference to any supporting evidence/reports of the
                            activity.
                        </li>
                        <li>
                            Receipt or other documenting evidence of
                            collection/delivery of the wastes.
                        </li>
                        <li>
                            Name, position and signature of the person in charge
                            of the activity.
                        </li>
                        <li>
                            Manufacturer and model of cleaning and capture
                            device as well as cleaning company executing the
                            cleaning.
                        </li>
                        <li>
                            Reference to test standard for which the method has
                            been tested, if relevant.
                        </li>
                    </ol>
                </div>
            </div>
            <div className="px-8 py-4 mt-4 newpage">
                {/* <!-- Section I --> */}
                <div className="mb-4">
                    <h3 className="mb-2 font-bold">
                        (D) Additional operational procedures and general remark
                    </h3>
                    <p className="italic text-gray-600">
                        This section is for recording any other inputs and
                        remarks not covered in the previous sections.
                    </p>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
    const inspectorStore = inspectorSelectors._getStore(store);
    const dateRange = inspectorSelectors.getDateRange(inspectorStore);
	const vesselStore = vesselSelectors._getStore(store);
	const vesselInfo = vesselSelectors.getVesselById(
		ownProps.vesselId,
		vesselSelectors.getVessels(vesselStore)
	);
    return {
        dateRange,
        vesselName: vesselInfo.vesselName,
        imoNumber: vesselInfo.imoNumber,
        tonnage: vesselInfo.tonnage,
        distinctiveNumberOrLetters: vesselInfo?.distinctiveNumberOrLetters,
    };
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
    return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(BFL1CoverPage);
